import { Link as ReeferLink } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';
import type { LinkProps } from 'react-router';
import { Link as RemixLink } from 'react-router';

import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';

import styles from './linkWithBasename.module.css';

interface LinkWithBasenameProps extends LinkProps {
  children: ReactNode;
  to: string;
  variant?: 'minimal' | 'reefer-underline';
}

/**
 * Renders a Remix Link setting href relative to Bloom Basename.
 * Wrapped around a Reefer Link to ensure consistent styling.
 * @example Usage w/ Reefer Style:
 * <LinkWithBasename to="/login" variant="reefer-underline">
 * </LinkWithBasename>
 */
export const LinkWithBasename = forwardRef<
  HTMLAnchorElement,
  LinkWithBasenameProps
>(function LinkWithBasename(
  { children, className, reloadDocument, to, variant = 'minimal', ...props },
  ref
) {
  const generatePath = useFullDestination();
  const toWithBasename = useMemo(() => generatePath(to), [to, generatePath]);

  return (
    <RemixLink
      ref={ref}
      className={clsx(
        styles['link'],
        {
          [styles['link--minimal']]: variant === 'minimal',
        },
        className
      )}
      to={toWithBasename}
      reloadDocument={
        reloadDocument ||
        /**
         * Force a full page refresh when linking to the menu home.
         * Otherwise with Single Fetch enabled, Remix makes a request to `/(:basename).data`,
         * which our reverse proxies do not route back to the Remix server.
         */
        to === '/'
      }
      {...props}
    >
      {variant === 'reefer-underline' ? (
        <ReeferLink
          onClick={
            // the onClick is a workaround to ensure that reefer doesn't make this unclickable
            () => ({})
          }
        >
          {children}
        </ReeferLink>
      ) : (
        children
      )}
    </RemixLink>
  );
});
